import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useModelService } from 'services/model/model.service';
import { DataResult } from 'models/data';
import { useAppStore } from 'hooks/state/useAppStore';
import { Interpretation } from 'components/MeasureComparison';
import { CountriesInfoContent } from './components/CountriesInfoContent';
import {
  PerformanceGapDisplay,
  PerformanceGapEmptyState,
} from 'pages/Benchmarking/common/performance-gap/PerformanceGapDisplay';
import { getCombinedBenchmarkEmptyStateType } from 'pages/Benchmarking/common/performance-gap/getBenchmarkEmptyStateType';
import { useBenchmarkDataResult } from 'pages/Benchmarking/queryHooks';
import { getErrorMessage } from 'utils/getErrorMessage';

interface CountriesDashboardInfo {
  data: DataResult;
  benchmark: string;
}

export function CountriesDashboard() {
  const { getMeasures, getAttribute } = useModelService();

  const chnCountry = getAttribute('chnCountry');
  const { benchmark } = useAppStore();

  const [dashboardData, setDashboardData] = useState<CountriesDashboardInfo>();

  const { countries } = useAppStore();

  const selectedCountryCodes = (countries ?? []).map(country => country.toString());

  const { data: inflightDashboardData, isPending, isError, error } = useBenchmarkDataResult('countries');

  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const countryDashboardInfo = {
      titleKey: 'Countries Benchmarking',
      subtitleKey: 'info.content.all_dates_utc',
      content: <CountriesInfoContent />,
    };

    setContent(countryDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  useEffect(() => {
    if (inflightDashboardData) {
      const filteredCountries = inflightDashboardData.sets.countries.filter(country =>
        selectedCountryCodes.length > 0 && country.attributes.chnCountry
          ? selectedCountryCodes.includes(country.attributes.chnCountry)
          : true
      );
      setDashboardData({
        ...inflightDashboardData,
        benchmark: benchmark,
        data: {
          ...inflightDashboardData,
          sets: {
            ...inflightDashboardData?.sets,
            countries: filteredCountries,
          },
        },
      });
    }
  }, [inflightDashboardData]);

  if (!dashboardData && !isError) {
    return <MetricsLoadingSpinner />;
  }

  if (isError) {
    const errorMessage = getErrorMessage(error);
    return (
      <PageContainer>
        <Tile
          titleId="benchmarking.countries"
          errorDetails={errorMessage}
          testId="benchmarking-countries-dashboard-error"
        />
      </PageContainer>
    );
  }

  if (!dashboardData) {
    return <MetricsLoadingSpinner />;
  }

  const [ordGmvVal, retQtyRateOD, cncQtyRate] = getMeasures(['ordGmvVal', 'retQtyRateExpOD', 'cncQtyRate']);

  const dataSet = dashboardData.data.sets.countries;
  const combinedReturnsAndCancellationsEmptyState = getCombinedBenchmarkEmptyStateType(
    dataSet,
    [retQtyRateOD, cncQtyRate],
    dashboardData.benchmark
  );

  return (
    <PageContainer>
      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.additional.performance" />
      </PageSectionTitle>
      <PerformanceGapDisplay
        dataSet={dataSet}
        improvementMeasure={ordGmvVal}
        groupingAttribute={chnCountry}
        interpretation={Interpretation.POSITIVE}
        drilldown
        benchmark={dashboardData.benchmark}
        isPending={isPending}
        opportunitiesTestId="bm-countries-growth-opportunities"
        chartTileTestId="bm-countries-growth-chart"
        chartTestId="bm-countries-ordered-gmv-growth"
      />
      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.returns-canellation" />
      </PageSectionTitle>
      {combinedReturnsAndCancellationsEmptyState ? (
        <PerformanceGapEmptyState
          type={combinedReturnsAndCancellationsEmptyState}
          testId="bm-countries-returns-cancellations-empty"
        />
      ) : (
        <>
          <PerformanceGapDisplay
            dataSet={dataSet}
            improvementMeasure={retQtyRateOD}
            groupingAttribute={chnCountry}
            interpretation={Interpretation.NEGATIVE}
            drilldown
            benchmark={dashboardData.benchmark}
            isPending={isPending}
            opportunitiesTestId="bm-countries-returns-opportunities"
            chartTileTestId="bm-countries-returns-chart"
            chartTestId="bm-countries-returns-chart-plot"
          />
          <PerformanceGapDisplay
            dataSet={dataSet}
            improvementMeasure={cncQtyRate}
            groupingAttribute={chnCountry}
            interpretation={Interpretation.NEGATIVE}
            drilldown
            benchmark={dashboardData.benchmark}
            isPending={isPending}
            opportunitiesTestId="bm-countries-cancellations-opportunities"
            chartTileTestId="bm-countries-cancellations-chart"
            chartTestId="bm-countries-cancellations-chart-plot"
          />
        </>
      )}
    </PageContainer>
  );
}
